import { useState, useEffect } from 'react';
import { questionService, assemblyService, propertyService } from 'services/api';
import { useAuth } from 'context/AuthContext';
import { quitarTemporizador } from 'components/TemporizadorVotacion';

const emptyForm = { assembly_id: '', question_text: '', question_type: 'yes_no', tipo_decision: '', options: '', max_choices: '' };
// Trim antes de exigir "no vacío" — un valor de solo espacios pasaba el
// `required` nativo del textarea (mismo hallazgo que en Property, ver #159).
const OPCION_VISUALMENTE_VACIA = /^[\s\p{Default_Ignorable_Code_Point}\u2800]*$/u;
function validarPregunta(form) {
  if (!form.question_text.trim()) return 'La pregunta es requerida';
  if (form.question_text.length > MAX_QUESTION_TEXT) return `La pregunta no puede tener más de ${MAX_QUESTION_TEXT} caracteres`;
  if (form.question_type === 'yes_no' && !form.tipo_decision) return 'Elige qué tipo de decisión es (económica, no económica o calificada).';
  if (form.question_type === 'multiple_choice' || form.question_type === 'one_choice') {
    const opciones = parseOptions(form.options);
    if (opciones.length < 2) return 'Debes agregar al menos 2 opciones de respuesta';
    if (opciones.length > MAX_OPCIONES) return `No puedes agregar más de ${MAX_OPCIONES} opciones`;
    // Mismo criterio que el backend: vacía = solo espacios o caracteres invisibles (ancho cero, braille en blanco…) que trim() no recorta.
    if (opciones.some(o => OPCION_VISUALMENTE_VACIA.test(o))) return 'Las opciones de respuesta no pueden estar vacías';
    if (opciones.some(o => o.length > MAX_LONGITUD_OPCION)) return `Cada opción no puede tener más de ${MAX_LONGITUD_OPCION} caracteres`;
    if (form.question_type === 'multiple_choice' && form.max_choices) {
      const mc = parseInt(form.max_choices, 10);
      if (!Number.isInteger(mc) || mc < 1 || mc > opciones.length) {
        return `"Cuántas opciones puede seleccionar" debe ser un número entre 1 y ${opciones.length}`;
      }
    }
  }
  return null;
}
const parseOptions = (str) => {
  if (!str) return [];
  if (Array.isArray(str)) return str;
  try { const p = JSON.parse(str); if (Array.isArray(p)) return p; } catch {}
  return String(str).split(',').map(s => s.trim()).filter(Boolean);
};
// Límites — mismos que valida el backend (questionsController.js).
const MAX_QUESTION_TEXT = 2000;
const MAX_OPCIONES = 20;
const MAX_LONGITUD_OPCION = 200;

// Lógica de la pantalla (estado, carga y acciones); la página solo pinta.
export default function useQuestions() {
  const { isSuperAdmin, isAssemblyAdmin, user, isReadOnly: globalIsReadOnly, getSubscriptionStateForProperty } = useAuth();

  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(false);   // no cargar al entrar
  const [show,       setShow]       = useState(false);
  const [form,       setForm]       = useState(emptyForm);
  const [faltaDecision, setFaltaDecision] = useState(false);
  const [errorForm,    setErrorForm]    = useState(null);   // error al guardar: se muestra DENTRO del modal
  const [activar,    setActivar]    = useState(null);   // pregunta sí/no por confirmar
  const [abriendo,   setAbriendo]   = useState(false);
  const [alert,      setAlert]      = useState(null);
  const [editing,    setEditing]    = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  // ── Datos para filtros ────────────────────────────────────────────────────
  const [properties,       setProperties]       = useState([]);
  const [assemblies,       setAssemblies]        = useState([]);
  const [loadingAssemblies, setLoadingAssemblies] = useState(false);

  // ── Filtros seleccionados — declarados ANTES de usarlos en cálculos ────────
  const [filterProp,       setFilterProp]       = useState('');
  const [filterAsm,        setFilterAsm]        = useState('');
  const [selectedPropObj,  setSelectedPropObj]  = useState(null);

  // isReadOnly efectivo según la propiedad seleccionada
  const isReadOnly = selectedPropObj
    ? getSubscriptionStateForProperty(selectedPropObj) === 'grace'
    : globalIsReadOnly;
  const selectedPropBlocked = selectedPropObj
    ? getSubscriptionStateForProperty(selectedPropObj) === 'blocked'
    : false;

  // Cargar propiedades/asambleas al montar según el rol
  useEffect(() => {
    if (isSuperAdmin) {
      propertyService.selector({ limit: 200 }).then(r => { setProperties(r.data.data || []); });
    } else if (isAssemblyAdmin && user?.assembly_id) {
      // assembly_admin: auto-cargar su asamblea directamente, sin selector
      const asmId = String(user.assembly_id);
      setLoadingAssemblies(true);
      assemblyService.getAll({ limit: 200 })
        .then(r => {
          setAssemblies(r.data.data || []);
          setFilterAsm(asmId);
          loadQuestions(asmId);
          setLoadingAssemblies(false);
        })
        .catch(() => setLoadingAssemblies(false));
    } else {
      // admin: cargar propiedades para validar membresía, y sus asambleas
      propertyService.selector({ limit: 200 }).then(r => {
        const props = r.data.data || [];
        setProperties(props);
        if (props.length === 1) {
          // 1 propiedad → auto-seleccionar y cargar asambleas de esa propiedad
          setFilterProp(String(props[0].id));
          setSelectedPropObj(props[0]);
          setLoadingAssemblies(true);
          assemblyService.getAll({ property_id: props[0].id, limit: 200 })
            .then(r2 => { setAssemblies(r2.data.data || []); setLoadingAssemblies(false); })
            .catch(() => setLoadingAssemblies(false));
        } else {
          // multi-prop: no cargar asambleas hasta que seleccione propiedad
          // (el selector de propiedad las cargará en handleFilterProp)
          setLoadingAssemblies(false);
        }
      });
    }
  }, [isSuperAdmin, isAssemblyAdmin, user?.assembly_id]); // eslint-disable-line

  // ── Al seleccionar propiedad (super_admin): cargar sus asambleas ──────────
  const handleFilterProp = (propId, elegida) => {
    setFilterProp(propId);
    setFilterAsm('');
    // Guardar objeto completo para calcular suscripción por propiedad
    const propObj = elegida || properties.find(p => String(p.id) === String(propId)) || null;
    setSelectedPropObj(propObj);
    setAssemblies([]);
    setItems([]);
    if (!propId) return;
    setLoadingAssemblies(true);
    assemblyService.getAll({ property_id: propId, limit: 200 })
      .then(r => { setAssemblies(r.data.data || []); setLoadingAssemblies(false); })
      .catch(() => setLoadingAssemblies(false));
  };

  // ── Al seleccionar asamblea: cargar sus preguntas ─────────────────────────
  const handleFilterAsm = (asmId) => {
    setFilterAsm(asmId);
    setItems([]);
    if (!asmId) return;
    loadQuestions(asmId);
  };

  const loadQuestions = (asmId) => {
    setLoading(true);
    const params = asmId ? { assembly_id: asmId } : {};
    questionService.getAll(params)
      .then(r => { setItems(Array.isArray(r.data) ? r.data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  // Reload después de guardar/eliminar
  const reload = () => { if (filterAsm) loadQuestions(filterAsm); };

  const save = async (e) => {
    e.preventDefault();
    setErrorForm(null);
    const campoError = validarPregunta(form);
    setFaltaDecision(form.question_type === 'yes_no' && !form.tipo_decision);
    if (campoError) return setErrorForm(campoError);
    try {
      if (editing) await questionService.update(editing.id, form);
      else         await questionService.create(form);
      setShow(false); setEditing(null); setForm(emptyForm);
      setAlert({ type: 'success', message: 'Guardado correctamente' });
      reload();
    } catch (err) {
      setErrorForm(err.response?.data?.message || 'Error al guardar');
    }
  };

  // F-17 (b): abrir una votación sí/no pasa por la confirmación de su regla.
  const toggle = (q) => {
    if (!q.active) return setActivar(q);   // abrir: modal (regla sí/no + temporizador)
    return cambiarEstado(q);
  };
  // Devuelve true si el cambio se hizo (para fijar o quitar el temporizador solo entonces).
  const cambiarEstado = async (q) => {
    try {
      if (q.active) await questionService.deactivate(q.id);
      else          await questionService.activate(q.id);
      if (q.active) quitarTemporizador(q.id);
      setAlert({ type: 'success', message: q.active ? 'Votación cerrada' : 'Votación abierta' });
      reload();
      return true;
    } catch (err) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Error al cambiar estado' });
      return false;
    }
  };

  const del = (q) => setConfirmDel(q);

  const confirmDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await questionService.delete(confirmDel.id);
      setConfirmDel(null);
      reload();
    } catch (err) { setAlert({ type: 'danger', message: err.response?.data?.message || 'Error al eliminar' }); setConfirmDel(null); }
    finally { setDeleting(false); }
  };

  const openEdit = (q) => {
    setEditing(q); setErrorForm(null);
    const optsStr = Array.isArray(q.options) ? q.options.join(', ') : (q.options || '');
    setForm({ assembly_id: q.assembly_id, question_text: q.question_text || '',
      question_type: q.question_type || 'yes_no', tipo_decision: q.tipo_decision || '', options: optsStr, max_choices: q.max_choices || '' });
    setShow(true);
  };

  const openNew = () => {
    setEditing(null); setErrorForm(null);
    // Preseleccionar la asamblea del filtro activo
    setForm({ ...emptyForm, assembly_id: filterAsm || '' });
    setShow(true);
  };

  const close = () => { setShow(false); setEditing(null); setForm(emptyForm); setFaltaDecision(false); setErrorForm(null); };
  const set   = (k, v) => { setErrorForm(null); setForm(f => ({ ...f, [k]: v })); };

  // Agrupar items por asamblea (vista simple dentro del filtro seleccionado)
  const groupedAsm = (() => {
    const map = {};
    items.forEach(q => {
      const asmId    = q.assembly_id;
      const asmLabel = q.assembly?.number_assembly ? `Asamblea #${q.assembly.number_assembly}` : `Asamblea #${asmId}`;
      if (!map[asmId]) map[asmId] = { id: asmId, label: asmLabel, questions: [], hasActive: false };
      map[asmId].questions.push(q);
      if (q.active) map[asmId].hasActive = true;
    });
    return Object.values(map).sort((a, b) => {
      if (a.hasActive && !b.hasActive) return -1;
      if (!a.hasActive && b.hasActive) return 1;
      return b.id - a.id;
    });
  })();

  // Nombres para subtítulos
  const selectedPropName = filterProp ? (selectedPropObj?.name || properties.find(p => String(p.id) === String(filterProp))?.name || '') : '';
  const selectedAsm      = filterAsm  ? assemblies.find(a => String(a.id) === String(filterAsm)) : null;
  const selectedAsmLabel = selectedAsm ? `Asamblea #${selectedAsm.number_assembly}` : '';

  // Condiciones de estado
  const needsPropSelect = (isSuperAdmin || (user?.role === 'admin' && !isAssemblyAdmin && properties.length > 1)) && !filterProp;
  const waitingProp = needsPropSelect;
  // assembly_admin nunca está en estado de espera — su asamblea carga automáticamente
  const waitingAsm  = !isAssemblyAdmin && !filterAsm && (isSuperAdmin ? !!filterProp : true);
  const canCreate   = !!filterAsm;

  return {
    items, loading, show, form, faltaDecision, errorForm, activar, setActivar, abriendo, setAbriendo,
    alert, setAlert, editing, confirmDel, setConfirmDel, deleting, properties, assemblies,
    loadingAssemblies, filterProp, filterAsm, isSuperAdmin, isAssemblyAdmin, user, isReadOnly,
    selectedPropBlocked, handleFilterProp, handleFilterAsm, save, toggle, cambiarEstado, del,
    confirmDelete, openEdit, openNew, close, set, groupedAsm, selectedPropName, selectedAsm,
    selectedAsmLabel, needsPropSelect, waitingProp, waitingAsm, canCreate,
  };
}
